import React from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const PartnershipCard = ({ partnership, onViewDetails }) => {
  const { t } = useTranslation();
  
  const getTierColor = (tier) => {
    const colors = {
      platinum: 'bg-gradient-to-br from-slate-400 to-slate-600',
      gold: 'bg-gradient-to-br from-yellow-400 to-yellow-600',
      silver: 'bg-gradient-to-br from-gray-300 to-gray-500'
    };
    return colors?.[tier] || colors?.silver;
  };

  const getTierIcon = (tier) => {
    const icons = {
      platinum: 'Crown',
      gold: 'Award',
      silver: 'Medal'
    };
    return icons?.[tier] || 'Medal';
  };

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden hover:shadow-lg transition-all duration-300 group">
      <div className="relative h-40 md:h-48 overflow-hidden">
        <Image
          src={partnership?.image}
          alt={partnership?.imageAlt}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background/90 to-transparent" />
        <div className="absolute top-3 right-3">
          <div className={`${getTierColor(partnership?.tier)} px-3 py-1 rounded-full flex items-center gap-1 shadow-lg`}>
            <Icon name={getTierIcon(partnership?.tier)} size={14} color="#FFFFFF" />
            <span className="text-xs font-semibold text-white capitalize">
              {t(`partnership.filters.${partnership?.tier}`)}
            </span>
          </div>
        </div>
        <div className="absolute bottom-3 left-3 right-3">
          <div className="flex items-center gap-2">
            <div className="w-12 h-12 md:w-14 md:h-14 bg-card rounded-lg p-2 shadow-lg">
              <Image
                src={partnership?.logo}
                alt={partnership?.logoAlt}
                className="w-full h-full object-contain"
              />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-base md:text-lg font-bold text-foreground font-headline truncate">
                {partnership?.name}
              </h3>
              <p className="text-xs md:text-sm text-muted-foreground truncate">
                {partnership?.league}
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="p-4 md:p-5 space-y-4">
        <div className="flex items-center gap-2 text-xs md:text-sm text-muted-foreground">
          <Icon name="MapPin" size={14} />
          <span>{partnership?.location}</span>
          <span className="mx-2">•</span>
          <Icon name="Calendar" size={14} />
          <span>{t('partnership.card.since')} {partnership?.since}</span>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div className="text-center p-2 bg-muted rounded-lg">
            <p className="text-lg md:text-xl font-bold text-foreground font-headline">
              {partnership?.transfers}
            </p>
            <p className="text-xs text-muted-foreground">{t('partnership.card.transfers')}</p>
          </div>
          <div className="text-center p-2 bg-muted rounded-lg">
            <p className="text-lg md:text-xl font-bold text-foreground font-headline">
              {partnership?.placements}
            </p>
            <p className="text-xs text-muted-foreground">{t('partnership.card.placements')}</p>
          </div>
          <div className="text-center p-2 bg-muted rounded-lg">
            <p className="text-lg md:text-xl font-bold text-foreground font-headline">
              {partnership?.active}
            </p>
            <p className="text-xs text-muted-foreground">{t('partnership.card.active')}</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {partnership?.specialties?.map((specialty, index) => (
            <span
              key={index}
              className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full"
            >
              {specialty}
            </span>
          ))}
        </div>

        <Button
          variant="outline"
          size="sm"
          iconName="ArrowRight"
          iconPosition="right"
          onClick={() => onViewDetails?.(partnership)}
          className="w-full"
        >
          {t('partnership.card.viewDetails')}
        </Button>
      </div>
    </div>
  );
};

export default PartnershipCard;